import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Box, Button, CircularProgress, Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import { CaretDown, Check } from "@phosphor-icons/react";
import { useAuth } from "../../../hooks/auth/useAuth";
import { useCommentsPost } from "../../../hooks/post/useCommentsPost";
import ServerError from "../ServerError/ServerError";
import { CommentsShow } from "./CommentsShow";
import { AddComment } from "./AddComment";
import { AnimatePresence, motion } from "framer-motion";

/** Те же три варианта, что и в приложении, и те же, что знает сервер. */
const SORT_OPTIONS = [
    { value: 'newest', label: 'Самые новые' },
    { value: 'oldest', label: 'Самые старые' },
    { value: 'likes', label: 'По лайкам' },
];

/**
 * Выбор порядка комментариев.
 *
 * Сортирует сервер, а не клиент: комментарии приходят страницами, и сортировка
 * уже загруженного куска показывала бы «самые старые» из последних двадцати.
 */
function CommentsSort({ sort, setSort }) {
    const current = SORT_OPTIONS.find(option => option.value === sort) ?? SORT_OPTIONS[0];

    return (
        <Dropdown>
            <MenuButton
                variant="plain"
                color="neutral"
                size="sm"
                endDecorator={<CaretDown size={14} />}
                sx={{ borderRadius: '999px', fontWeight: 400, opacity: 0.7 }}
            >
                {current.label}
            </MenuButton>
            <Menu placement="bottom-end" variant="plain" sx={{ borderRadius: '12px' }}>
                {SORT_OPTIONS.map(option => (
                    <MenuItem
                        key={option.value}
                        selected={option.value === sort}
                        onClick={() => setSort(option.value)}
                        sx={{ py: 0.75, gap: 1 }}
                    >
                        {option.label}
                        {option.value === sort && <Check size={16} style={{ marginLeft: 'auto' }} />}
                    </MenuItem>
                ))}
            </Menu>
        </Dropdown>
    );
}

function AuthStub() {
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px dashed",
                borderColor: "neutral.outlinedBorder",
                textAlign: "center",
                mb: 2,
            }}
        >
            <Typography mb={2}>
                Чтобы оставить комментарий, нужно быть авторизованным
            </Typography>
            <Button
                size="sm"
                onClick={() => navigate("/auth")}
                sx={{
                    borderRadius: '50px',
                    px: 2,
                }}
            >
                Войти или зарегистрироваться
            </Button>
        </Box>
    );
}

/**
 * Заглушка вместо поля ввода.
 *
 * Причины разные — блокировка собеседником или персональный запрет
 * комментировать, — но вид один: писать нельзя, и поле ввода показывать
 * бессмысленно, оно бы только собирало текст в никуда.
 */
function BlockedStub({ text = 'Комментарии недоступны' }) {
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px dashed",
                borderColor: "neutral.outlinedBorder",
                textAlign: "center",
                mb: 2,
                color: "text.tertiary",
            }}
        >
            <Typography>{text}</Typography>
        </Box>
    );
}

function CorrectComments({
    comments = [],
    globalPostId = 0,
    // Неугадываемый адрес записи — им форма подтверждает переход по ссылке
    // для записи «по ссылке» (см. TemplateComments).
    linkHash = null,
    countComments,
    blocked = false,
    canComment = true,
    upperCountComments = () => { },
    downCountComments = () => { },
}) {
    const [newComments, setNewComments] = useState([]);
    const [showComments, setShowComments] = useState([]);
    // На чей комментарий отвечаем. Держим здесь, а не в форме: кнопку
    // «Ответить» нажимают в ленте, а плашку показывает форма.
    const [replyTarget, setReplyTarget] = useState(null);

    useEffect(() => {
        setShowComments(comments);
    }, [comments]);

    const { isAuth } = useAuth();

    const handlerNewComment = (comment) => {
        upperCountComments();
        setNewComments([
            // Ответ сервер возвращает без развёрнутой цитаты (в ней только
            // replyId) — собираем её из того, кому отвечали, иначе свежий ответ
            // остался бы без цитаты до перезагрузки страницы.
            comment?.replyId && replyTarget?.id === comment.replyId
                ? { ...comment, replyTo: replyTarget.replyTo }
                : comment,
            ...newComments
        ]);
    }

    const handlerReply = (comment) => {
        const name = [comment?.user?.firstName, comment?.user?.lastName]
            .filter(Boolean).join(' ') || 'Пользователь';

        const plain = String(comment?.content ?? '').replace(/<[^>]*>/g, '').trim();

        setReplyTarget({
            id: comment.id,
            name,
            replyTo: {
                id: comment.id,
                userId: comment?.user?.id,
                content: plain.length > 120 ? `${plain.slice(0, 120)}…` : plain,
                user: comment?.user ?? null,
            },
        });

        document.getElementById('comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const onDelete = (id) => {
        setShowComments(prev => prev.filter(c => c.id !== id));
        setNewComments(prev => prev.filter(c => c.id !== id));
        downCountComments();
    }

    const onEdit = (id, newContent) => {
        setShowComments(prev => prev.map(c => (c.id === id ? { ...c, content: newContent } : c)));
        setNewComments(prev => prev.map(c => (c.id === id ? { ...c, content: newContent } : c)));
    };

    return (
        <>
            {!isAuth ? (
                <AuthStub />
            ) : blocked ? (
                <BlockedStub />
            ) : !canComment ? (
                <BlockedStub text="Вам запрещено оставлять комментарии" />
            ) : (
                <AddComment
                    globalPostId={globalPostId}
                    linkHash={linkHash}
                    setNewComments={handlerNewComment}
                    replyTarget={replyTarget}
                    onCancelReply={() => setReplyTarget(null)}
                />
            )}

            {countComments === 0 && (
                <Typography color="neutral">
                    Комментариев нет.
                </Typography>
            )}

            {countComments > 0 && (
                <CommentsShow
                    comments={showComments ?? []}
                    newComments={newComments ?? []}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    // Комментирование закрыто — поля ввода нет, и «Ответить»
                    // вело бы в никуда.
                    onReply={isAuth && !blocked && canComment ? handlerReply : null}
                />
            )}
        </>
    )
}

function ShowCountComments({ countComments = 0 }) {
    return (
        <AnimatePresence mode="wait">
            <motion.span
                key={countComments}
                initial={{ opacity: 0, y: -6, filter: "blur(4px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: 6, filter: "blur(4px)" }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                style={{ display: "inline-block", marginLeft: 8 }}
            >
                {countComments}
            </motion.span>
        </AnimatePresence>
    );
}

/**
 * @param {Object} props
 * @param {number} props.globalPostId - сквозной id записи
 * @param {string} [props.linkHash] - неугадываемый адрес записи. Комментарии
 *   закрыты вместе с записью, и для доступа «по ссылке» сервер требует именно
 *   его: сквозной id перебирается (см. server/services/viewerContext.js).
 */
export default function TemplateComments({ globalPostId = 0, linkHash = null }) {
    const [sort, setSort] = useState('newest');
    const { comments, restriction, error, loading } = useCommentsPost(globalPostId, sort, linkHash);
    const [countComments, setCountComments] = useState(-1);

    useEffect(() => {
        if (!error) {
            setCountComments((comments ?? []).length);
        }
    }, [loading, error, comments]);

    const upperCountComments = () =>
        setCountComments(countComments + 1);

    const downCountComments = () =>
        setCountComments(Math.max(0, countComments - 1));

    const { hash } = useLocation();

    useEffect(() => {
        if (!hash || countComments < 0) return;

        const el = document.getElementById(hash.slice(1));
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [hash, countComments]);

    return (
        <Box my={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                <Typography level="title-lg" id="comments">
                    Комментарии
                    {!error && countComments > 0 && (
                        <ShowCountComments countComments={countComments} />
                    )}
                </Typography>

                {/* Порядок предлагаем, только когда есть что упорядочивать. */}
                {!error && countComments > 1 && (
                    <CommentsSort sort={sort} setSort={setSort} />
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <ServerError desc="Не удалось загрузить комментарии" />
            ) : (
                <CorrectComments
                    comments={comments ?? []}
                    globalPostId={globalPostId}
                    linkHash={linkHash}
                    countComments={countComments}
                    blocked={restriction?.blocked ?? false}
                    canComment={restriction?.canComment ?? true}
                    upperCountComments={upperCountComments}
                    downCountComments={downCountComments}
                />
            )}
        </Box>
    );
}
